import { useEffect, useMemo, useState, type ReactNode } from 'react';
import './ProfileDataFormView.scss';
import { TabPanel, TabView } from 'primereact/tabview';
import 'primeicons/primeicons.css';
import { getSelectableBuildings, loadProfileDataFormForUser, saveProfileDataFormForUser } from '../../../servicesHooks/ProfileDataForm/useProfileDataFormService';
import { DEFAULT_BUILDING_NOT_SELECTABLE, parseApiError } from '../../../services/apiError';
import { ComboDataDTO } from '../../../dto/ComboDataDTO';
import ConsentTab from './tabs/ConsentTab';
import PersonalTab from './tabs/PersonalTab';
import { defaultProfileDataFormDTO, ProfileDataFormDTO } from '../../../dto/profiledata/ProfileDataFormDTO';
import ProfessionalTab from './tabs/ProfessionalTab';
import InvoiceTab from './tabs/InvoiceTab';
import UserConfigTab from './tabs/UserConfigTab';
import { ETypeOfNotification, setNotification, useFormValidation } from 'medere-ui-framework';
import { useDispatch } from 'react-redux';
import { HttpStatusCode } from 'axios';
import useResizeListener from '../../../hooks/useResizeListener';
import MobileTabSelector, { MobileTabSelectorOption } from '../../Location/components/MobileTabSelector';

type ProfileTabKey = 'personal' | 'professional' | 'invoice' | 'config' | 'consent';

interface ProfileTabConfig {
    key: ProfileTabKey;
    label: string;
    content: ReactNode;
    className?: string;
}

interface UserConfigErrors {
    username: boolean;
    password: boolean;
    confirmpassword: boolean;
}

export default function ProfileDataFormView() {

    const dispatch = useDispatch();
    const isMobile = useResizeListener(768);
    const [profileDataForm, setProfileDataForm] = useState<ProfileDataFormDTO>(defaultProfileDataFormDTO);
    const [activeTab, setActiveTab] = useState(0);
    const [userConfigErrors, setUserConfigErrors] = useState<UserConfigErrors>({
        username: false,
        password: false,
        confirmpassword: false,
    });

    const { errors: personalDataErrors, handleValidation: handlePersonalDataValidation } = useFormValidation(profileDataForm.personalData);
    const { errors: professionalDataErrors, handleValidation: handleProfessionalDataValidation } = useFormValidation(profileDataForm.professionalData);

    const [buildingOptions, setBuildingOptions] = useState<ComboDataDTO[]>([]);
    const [defaultBuildingError, setDefaultBuildingError] = useState<string | undefined>();

    function loadBuildingOptions() {
        getSelectableBuildings().then(setBuildingOptions).catch((error) => {
            console.error(error);
            setBuildingOptions([]);
        });
    }

    useEffect(() => {
        loadProfileDataFormForUser().then((response) => {
            setProfileDataForm(response);
        }).catch((error) => {
            console.error(error);
        });
        loadBuildingOptions();
    }, []);

    const tabsConfig = useMemo<ProfileTabConfig[]>(() => {
        const baseTabs: ProfileTabConfig[] = [
            {
                key: 'personal',
                label: 'Personales',
                className: 'custom-tabpanel',
                content: (
                    <PersonalTab
                        personalData={profileDataForm.personalData}
                        setPersonalData={(personalData) => setProfileDataForm(prevState => ({ ...prevState, personalData }))}
                        isProfessional={profileDataForm.isprofessional}
                        errors={personalDataErrors}
                    />
                ),
            },
            {
                key: 'config',
                label: 'Configuracion',
                content: (
                    <UserConfigTab
                        userConfigData={profileDataForm.userConfigData}
                        setConfigData={(userConfigData) => {
                            setProfileDataForm(prevState => ({ ...prevState, userConfigData }));
                            setUserConfigErrors({
                                username: false,
                                password: false,
                                confirmpassword: false,
                            });
                            setDefaultBuildingError(undefined);
                        }}
                        buildingOptions={buildingOptions}
                        defaultBuildingError={defaultBuildingError}
                        errors={userConfigErrors}
                    />
                ),
            },
            {
                key: 'consent',
                label: 'Consentimiento',
                content: <ConsentTab />,
            },
        ];

        if (profileDataForm.isprofessional) {
            baseTabs.splice(
                1,
                0,
                {
                    key: 'professional',
                    label: 'Profesionales',
                    content: (
                        <ProfessionalTab
                            professionalData={profileDataForm.professionalData}
                            setProfessionalData={(professionalData) => setProfileDataForm(prevState => ({ ...prevState, professionalData }))}
                            secretary={!profileDataForm.isprofessional}
                            isProfessional={profileDataForm.isprofessional}
                            errors={professionalDataErrors}
                        />
                    ),
                },
                {
                    key: 'invoice',
                    label: 'Facturacion',
                    content: (
                        <InvoiceTab
                            invoiceData={profileDataForm.invoiceData}
                            setInvoiceData={(invoiceData) => setProfileDataForm(prevState => ({ ...prevState, invoiceData }))}
                        />
                    ),
                },
            );
        }

        return baseTabs;
    }, [
        profileDataForm.personalData,
        profileDataForm.isprofessional,
        profileDataForm.professionalData,
        profileDataForm.invoiceData,
        profileDataForm.userConfigData,
        personalDataErrors,
        professionalDataErrors,
        userConfigErrors,
        buildingOptions,
        defaultBuildingError,
    ]);

    const mobileTabOptions = useMemo<MobileTabSelectorOption[]>(() => {
        return tabsConfig.map((tab, index) => ({
            id: index,
            label: tab.label,
        }));
    }, [tabsConfig]);

    useEffect(() => {
        setActiveTab((currentTab) => {
            if (tabsConfig.length === 0) return 0;
            if (currentTab < tabsConfig.length) return currentTab;
            return tabsConfig.length - 1;
        });
    }, [tabsConfig.length]);

    function callValidationErrorNotification() {
        dispatch(setNotification({ title: "No pudimos guardar los datos", desc: "Te indicamos con color los campos a repasar", icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
    }

    function getTabIndexByKey(tabKey: ProfileTabKey) {
        return tabsConfig.findIndex((tab) => tab.key === tabKey);
    }

    function navigateToTab(tabKey: ProfileTabKey, fallbackTabKey: ProfileTabKey = 'personal') {
        const tabIndex = getTabIndexByKey(tabKey);
        if (tabIndex >= 0) {
            setActiveTab(tabIndex);
            return;
        }
        const fallbackIndex = getTabIndexByKey(fallbackTabKey);
        setActiveTab(fallbackIndex >= 0 ? fallbackIndex : 0);
    }

    function validate() {
        if (!validateUserConfigData()) {
            navigateToTab('config', 'personal');
            dispatch(setNotification({ title: "No pudimos guardar los datos", desc: "Revisá el usuario y la contraseña antes de continuar", icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
            return;
        }

        if (tabsConfig[activeTab]?.key === 'config') {
            handleSaveProfileData();
            return;
        }

        handlePersonalDataValidation(() => {
            handleProfessionalDataValidation(() => {
                handleSaveProfileData();
            }, undefined, ['prefix', 'specialities', 'secretaries', 'signaturedata'],
                () => {
                    navigateToTab('professional', 'personal');
                    callValidationErrorNotification();
                });
        }, undefined, ['address'],
            () => {
                navigateToTab('personal', 'personal');
                callValidationErrorNotification();
            });
        return;
    }

    function validateUserConfigData() {
        const { username, password, confirmpassword } = profileDataForm.userConfigData;
        const hasPasswordChange = !!password?.trim() || !!confirmpassword?.trim();

        const nextErrors: UserConfigErrors = {
            username: !username?.trim(),
            password: hasPasswordChange && !password?.trim(),
            confirmpassword: hasPasswordChange && (!confirmpassword?.trim() || password !== confirmpassword),
        };

        setUserConfigErrors(nextErrors);
        return !Object.values(nextErrors).some(Boolean);
    }

    function handleSaveProfileData() {
        saveProfileDataFormForUser(profileDataForm).then(res => {
            if (res.status != HttpStatusCode.Ok) {
                dispatch(setNotification({ title: "No pudimos guardar los datos", desc: res.data, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                return;
            }
            setUserConfigErrors({
                username: false,
                password: false,
                confirmpassword: false,
            });
            dispatch(setNotification({ title: "Datos guardados", icon: 'pi-check-circle', typeOfNotification: ETypeOfNotification.INFO }));
        }).catch((error) => {
            const apiError = parseApiError(error);
            if (apiError.errorCode === DEFAULT_BUILDING_NOT_SELECTABLE) {
                // Se inactivó o dejó de ser elegible mientras se editaba: se marca el campo y se refrescan las opciones.
                setDefaultBuildingError(apiError.message);
                loadBuildingOptions();
                navigateToTab('config', 'personal');
            }
            dispatch(setNotification({ title: "No pudimos guardar los datos", icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
        });

    }

    return (
        <div className='profile-data-form-view viewContainer' style={{ padding: '15px', backgroundColor: 'white' }}>
            <h1 style={{ marginTop: '8px', fontWeight: 600, color: '#B67345', fontSize: '22px', textAlign: 'left', marginBottom: '10px' }}>Datos de perfil</h1>
            {isMobile ? (
                <>
                    <MobileTabSelector
                        id='profiledatatabselectorid'
                        className='profile-mobile-tab-selector'
                        value={activeTab}
                        options={mobileTabOptions}
                        onChange={(id) => setActiveTab(Number(id))}
                        ariaLabel='Seleccionar tab de datos de perfil'
                    />
                    <div className='profile-mobile-tab-content'>
                        {tabsConfig[activeTab]?.content}
                    </div>
                </>
            ) : (
                <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                    {tabsConfig.map((tab) => (
                        <TabPanel key={tab.key} header={tab.label} className={tab.className}>
                            {tab.content}
                        </TabPanel>
                    ))}
                </TabView>
            )}
            <div className="button-container">
                <button className="save-button" onClick={validate}>Guardar datos</button>
            </div>
        </div>
    );
}
